import { type WeeklySchedule } from "@clinic/shared";
import { useEffect, useState, type JSX } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";
import { Badge, PageHeader, PersonName, useToast } from "@clinic/ui";
import { WorkingHours } from "@web/shared/components/working-hours";
import { weekFitsWithin } from "@web/shared/lib/week";
import { SkeletonForm } from "@clinic/ui/components/skeleton";
import { useSession } from "@web/shared/providers/session";
import { canEditAnySchedule } from "@web/shared/permissions/doctors";
import { PAGE_CAPABILITIES, mayOpen } from "@web/shared/lib/navigation";
import { useClinic } from "@web/shared/queries/clinic";
import { useDoctor, useUpdateDoctorSchedule } from "@web/modules/doctors/queries";
import { ExtraHoursPanel } from "@web/modules/schedule/components/extra-hours-panel";
import { TimeOffPanel } from "@web/modules/schedule/components/time-off-panel";
import { errorToast } from "@web/shared/lib/api-error";
import { setClinicTimeZone } from "@web/shared/lib/clinic-locale";
import { useDelayedLoading } from "@clinic/ui/lib/use-delayed-loading";
import { UnsavedChanges } from "@web/shared/components/unsaved-changes";
import { countChanges } from "@web/shared/lib/changes";
import { useLocalized } from "@web/shared/hooks/use-localized";

export function DoctorPage(): JSX.Element {
  const { t } = useTranslation();
  const text = useLocalized();
  const toast = useToast();
  const { id } = useParams<{ id: string }>();
  const { user, can } = useSession();

  const doctor = useDoctor(id);
  const showSkeleton = useDelayedLoading(doctor.isPending);
  const clinic = useClinic();
  const updateSchedule = useUpdateDoctorSchedule();

  const [schedule, setSchedule] = useState<WeeklySchedule>([]);

  useEffect(() => {
    if (doctor.data) {
      setSchedule(doctor.data.weeklySchedule);
    }
  }, [doctor.data]);

  useEffect(() => {
    setClinicTimeZone(clinic.data);
  }, [clinic.data]);

  if (id === undefined) {
    return <Navigate to="/users?view=doctors" replace />;
  }

  if (doctor.isPending) {
    return showSkeleton ? <SkeletonForm fields={5} /> : <></>;
  }

  if (!doctor.data) {
    return <Navigate to="/users?view=doctors" replace />;
  }

  const isOwn = doctor.data.userId === user?.id;
  const canEdit = canEditAnySchedule(can) || isOwn;
  const clinicHours = clinic.data?.workingHours ?? [];
  const fits = weekFitsWithin(schedule, clinicHours);
  const changes = countChanges(doctor.data.weeklySchedule, schedule);

  const save = async (): Promise<void> => {
    try {
      await updateSchedule.mutateAsync({ id, weeklySchedule: schedule });
      toast.success("doctors.scheduleUpdated");
    } catch (error) {
      toast.error(...errorToast(error));
    }
  };

  return (
    <div data-testid="doctor-page" className="flex flex-col gap-5">
      <PageHeader
        data-testid="doctor-header"
        title="doctors.pageTitle"
        subtitle="doctors.pageSubtitle"
      />

      <div className="flex flex-wrap items-center gap-3">
        <PersonName
          name={doctor.data.user.name}
          showBoth
          data-testid="doctor-name"
          className="text-section font-medium text-ink"
        />
        <Badge tone="info" data-testid="doctor-specialty">
          {text(doctor.data.specialty.name)}
        </Badge>
        <span className="text-label text-ink-muted">
          {doctor.data.defaultAppointmentDurationMinutes} {t("doctors.durationUnit")}
        </span>
        {mayOpen(PAGE_CAPABILITIES.payroll, can) && (
          <Link
            to={`/payroll/${doctor.data.userId}`}
            data-testid="doctor-pay-link"
            className="ms-auto text-label font-medium text-primary-700 hover:underline"
          >
            {t("doctors.payStatement")}
          </Link>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section
          data-testid="doctor-schedule"
          className="border border-line rounded-card bg-surface shadow-card p-4"
        >
          <h2 className="mb-3 text-value font-medium text-ink">{t("doctors.schedule")}</h2>

          <WorkingHours
            value={schedule}
            onChange={setSchedule}
            disabled={!canEdit}
            idPrefix="doctor-hours"
            within={clinicHours}
            withinLabel={t("schedule.clinicHours")}
          />

          {!fits && (
            <p data-testid="doctor-schedule-warning" className="mt-3 text-label text-warning-700">
              {t("schedule.outsideBounds", { bounds: t("schedule.clinicHours") })}
            </p>
          )}
        </section>

        <section
          data-testid="doctor-time-off"
          className="border border-line rounded-card bg-surface shadow-card p-4"
        >
          <TimeOffPanel doctorId={id} canEdit={canEdit} />
        </section>

        <section
          data-testid="doctor-extra-hours"
          className="border border-line rounded-card bg-surface shadow-card p-4"
        >
          <ExtraHoursPanel doctorId={id} canEdit={canEdit} />
        </section>
      </div>

      {canEdit && (
        <UnsavedChanges
          data-testid="doctor-unsaved"
          dirty={changes > 0}
          count={changes}
          saving={updateSchedule.isPending}
          invalid={!fits}
          onSave={() => void save()}
          onDiscard={() => setSchedule(doctor.data.weeklySchedule)}
        />
      )}
    </div>
  );
}
